import { useEffect, useState } from "react";
import { ColumnsType } from "antd/lib/table";
import { useAsyncAxios, utilAxiosWithAuth } from "../utils/customAxios";
import { Modal, Space } from "antd";

import * as Types from "../commons/typeDTO";
import * as Strings from "../commons/string";
import * as Common from "../commons/common";
import * as Utils from "../utils/utils";
import * as Request from "../commons/request";

import RentDetail from "./RentDetail";
import RentUserDetail from "./RentUserDetail";
import RentImageViewer from "./RentImageViewer";
import RentPaymentViewer from "./RentPaymentViewer";
import moment from "moment";
import DataTable from "./DataTable";

function MyRent({ rents }: { rents?: Array<Types.RentDto> }) {
    const [currentRent, setCurrentRent] = useState<Types.RentDto>();
    const [paymentItems, setPaymentItems] = useState<Array<Types.RentPaymentItemDto>>([]);
    const [cancelRent, setCancelRent] = useState<Types.RentDto>();
    const [openModalCancelReservation, setOpenModalCancelReservation] = useState(false);

    useEffect(() => {
        requestGetPaymentItems();
    }, [rents]);

    const columns: ColumnsType<Types.RentDto> = [
        {
            align: "center",
            title: "번호",
            dataIndex: "num",
            key: "rentId",
            render: (item, record, index) => <>{rents && rents.indexOf(record) + 1}</>,
        },
        {
            align: "center",
            title: "예약일시",
            dataIndex: "createdDate",
            key: "createdDate",
            sorter: (a, b, sortOrder) => Utils.sortString(a.startDate.valueOf(), b.startDate.valueOf(), sortOrder),
            render: (_, row) => {
                return row.createdDate ? Utils.utcToLocalTime(row.createdDate, Common.FORMAT_DATE_TIME).substring(2, 16) : Strings.dash;
            },
        },
        {
            align: "center",
            title: "이용일",
            dataIndex: "startDate",
            key: "startDate",
            sorter: (a, b, sortOrder) => Utils.sortString(a.startDate.valueOf(), b.startDate.valueOf(), sortOrder),
            render: (_, row) => {
                return row.startDate ? row.startDate.substring(2) : Strings.dash;
            },
        },
        {
            align: "center",
            title: "반납일",
            dataIndex: "endDate",
            key: "endDate",
            sorter: (a, b, sortOrder) => Utils.sortString(a.endDate.valueOf(), b.endDate.valueOf(), sortOrder),
            render: (_, row) => {
                return row.endDate ? row.endDate.substring(2) : Strings.dash;
            },
        },
        {
            align: "center",
            title: "차량번호",
            dataIndex: "carNumber",
            key: "carNumber",
            sorter: (a, b, sortOrder) => Utils.sortString(a.rentCar.car.carNumber.valueOf(), b.rentCar.car.carNumber.valueOf(), sortOrder),
            render: (_, row) => {
                if (row.rentCar?.car?.carNumber) {
                    return row.rentCar?.car?.carNumber;
                } else return Strings.dash;
            },
        },
        {
            align: "center",
            title: "예약번호",
            dataIndex: "rentNo",
            key: "rentNo",
            sorter: (a, b, sortOrder) => Utils.sortString(a.rentNo.valueOf(), b.rentNo.valueOf(), sortOrder),
            render: (_, row) => {
                if (row.rentNo) {
                    return row.rentNo;
                } else return Strings.dash;
            },
        },
        {
            align: "center",
            title: "다날주문번호",
            dataIndex: "danal",
            key: "danal",
            sorter: (a, b, sortOrder) => {
                const aPayment = Utils.getReservationPayment(a.rentPayments);
                const bPayment = Utils.getReservationPayment(b.rentPayments);

                return Utils.sortString(
                    aPayment !== null ? aPayment.paymentImp.impUid.valueOf() : Strings.dash.valueOf(),
                    bPayment !== null ? bPayment.paymentImp.impUid.valueOf() : Strings.dash.valueOf(),
                    sortOrder
                );
            },
            render: (_, row) => {
                if (row.rentPayments) {
                    return Utils.getReservationPayment(row.rentPayments)?.paymentImp?.impUid;
                } else return Strings.dash;
            },
        },
        {
            align: "center",
            title: "예약상태",
            dataIndex: "rentStatus",
            key: "rentStatus",
            sorter: (a, b, sortOrder) => Utils.sortString(Utils.getRentStatus(a).valueOf(), Utils.getRentStatus(b).valueOf(), sortOrder),
            render: (_, row) => {
                return Utils.getRentStatus(row);
            },
        },
        {
            align: "center",
            title: "총 정산요금",
            dataIndex: "amount",
            key: "amount",
            sorter: (a, b, sortOrder) => {
                const aPayment = Utils.getAllPaidPaymentAmount(a.rentPayments);
                const bPayment = Utils.getAllPaidPaymentAmount(b.rentPayments);

                return Utils.sortString(String(aPayment).valueOf(), String(bPayment).valueOf(), sortOrder);
            },
            render: (_, row) => {
                return Utils.convertCurrencyKorean(Utils.getAllPaidPaymentAmount(row.rentPayments));
            },
        },
    ];

    //get rents
    const requestAxiosGetRents = async (rentStatus?: string, keywords?: string) => {
        let response;
        if ((rentStatus !== undefined && rentStatus.length > 0) || (keywords !== undefined && keywords.length > 0)) {
            response = await utilAxiosWithAuth().get(Request.RENT_ALL_URL, {
                params: {
                    rentStatus: rentStatus,
                    keyWord: keywords,
                },
            });
        } else {
            response = await utilAxiosWithAuth().get(Request.RENTS_URL);
        }
        return response.data;
    };

    const { loading: loadingGetRents, error: errorGetRents, data: resultGetRents, execute: requestGetRents } = useAsyncAxios(requestAxiosGetRents);

    useEffect(() => {
        if (resultGetRents === null) return;
        console.log("resultGetRents", resultGetRents);

        if (currentRent !== undefined) {
            const currentRents = resultGetRents.rents.filter((rent: Types.RentDto) => rent.rentId === currentRent.rentId);
            if (currentRents.length > 0) setCurrentRent(currentRents[0]);
        }
    }, [resultGetRents]);

    useEffect(() => {
        if (errorGetRents === null) return;
        console.log("errorGetRents", errorGetRents);
    }, [errorGetRents]);

    //get payment items
    const requestAxiosGetPaymentItems = async () => {
        const response = await utilAxiosWithAuth().get(Request.RENT_PAYMENT_ITEMS_URL);
        return response.data;
    };

    const {
        loading: loadingGetPaymentItems,
        error: errorGetPaymentItems,
        data: resultGetPaymentItems,
        execute: requestGetPaymentItems,
    } = useAsyncAxios(requestAxiosGetPaymentItems);

    useEffect(() => {
        if (resultGetPaymentItems === null) return;

        console.log("resultGetPaymentItems", resultGetPaymentItems);
        setPaymentItems(resultGetPaymentItems.rentPaymentItems);
    }, [resultGetPaymentItems]);

    useEffect(() => {
        if (errorGetPaymentItems === null) return;

        console.log("errorGetPaymentItems", errorGetPaymentItems);
    }, [errorGetPaymentItems]);

    //update use rent
    const requestAxiosUseRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId + Request.RENT_USE_URL);
        return response.data;
    };

    const { loading: loadingUseRent, error: errorUseRent, data: resultUseRent, execute: requestUseRent } = useAsyncAxios(requestAxiosUseRent);

    useEffect(() => {
        if (resultUseRent === null) return;

        console.log("resultUseRent", resultUseRent);
        requestGetRents();
    }, [resultUseRent]);

    useEffect(() => {
        if (errorUseRent === null) return;
        console.log("errorUseRent", errorUseRent);
    }, [errorUseRent]);

    //update return rent
    const requestAxiosReturnRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId + Request.RENT_RETURN_URL);
        return response.data;
    };

    const { loading: loadingReturnRent, error: errorReturnRent, data: resultReturnRent, execute: requestReturnRent } = useAsyncAxios(requestAxiosReturnRent);

    useEffect(() => {
        if (resultReturnRent === null) return;
        console.log("resultReturnRent", resultReturnRent);
        requestGetRents();
        // requestGetStartRents();
        // requestGetEndRents();
    }, [resultReturnRent]);

    useEffect(() => {
        if (errorReturnRent === null) return;

        console.log("errorReturnRent", errorReturnRent);
    }, [errorReturnRent]);

    //update arrived rent
    const requestAxiosArrivedRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId + Request.RENT_ARRIVE_URL);
        return response.data;
    };

    const {
        loading: loadingArrivedRent,
        error: errorArrivedRent,
        data: resultArrivedRent,
        execute: requestArrivedRent,
    } = useAsyncAxios(requestAxiosArrivedRent);

    useEffect(() => {
        if (resultArrivedRent === null) return;

        console.log("resultArrivedRent", resultArrivedRent);
        if (currentRent) {
            if (addBatteryPayment(currentRent) === false) addDelayedArrivePayment(currentRent);
        }
        requestReturnRent(resultArrivedRent.rent.rentId);
        requestGetRents();
    }, [resultArrivedRent]);

    useEffect(() => {
        if (errorArrivedRent === null) return;

        console.log("errorArrivedRent", errorArrivedRent);
    }, [errorArrivedRent]);

    //update change rent car
    const requestAxiosChangeRentCar = async (rentId: number, rentCarId: number) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, { rentCar: { rentCarId: rentCarId } });
        return response.data;
    };

    const {
        loading: loadingChangeRentCar,
        error: errorChangeRentCar,
        data: resultChangeRentCar,
        execute: requestChangeRentCar,
    } = useAsyncAxios(requestAxiosChangeRentCar);

    useEffect(() => {
        if (resultChangeRentCar === null) return;

        console.log("resultChangeRentCar", resultChangeRentCar);
        requestGetRents();
    }, [resultChangeRentCar]);

    useEffect(() => {
        if (errorChangeRentCar === null) return;

        console.log("errorChangeRentCar", errorChangeRentCar);
    }, [errorChangeRentCar]);

    //update rent detail (memo, checkedUsagePolicy, checkedExitPolicy)
    const requestAxiosChangeRentDetail = async (rentId: number, memo: string, checkedUsagePolicy: boolean, checkedExitPolicy: boolean) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, {
            memo: memo,
            checkedUsagePolicy: checkedUsagePolicy,
            checkedExitPolicy: checkedExitPolicy,
        });
        return response.data;
    };

    const {
        loading: loadingChangeRentDetail,
        error: errorChangeRentDetail,
        data: resultChangeRentDetail,
        execute: requestChangeRentDetail,
    } = useAsyncAxios(requestAxiosChangeRentDetail);

    useEffect(() => {
        if (resultChangeRentDetail === null) return;

        console.log("resultChangeRentDetail", resultChangeRentDetail);
        requestGetRents();
    }, [resultChangeRentDetail]);

    useEffect(() => {
        if (errorChangeRentDetail === null) return;

        console.log("errorChangeRentDetail", errorChangeRentDetail);
    }, [errorChangeRentDetail]);

    //update paid item
    const requestAxiosUpdatePaidItem = async (rentId: number, paidItemIds: Array<number>) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_URL + rentId, {
            rentPaidItems: paidItemIds.map((paidItemId) => {
                return { rentPaidItemId: paidItemId };
            }),
        });
        return response.data;
    };

    const {
        loading: loadingUpdatePaidItem,
        error: errorUpdatePaidItem,
        data: resultUpdatePaidItem,
        execute: requestUpdatePaidItem,
    } = useAsyncAxios(requestAxiosUpdatePaidItem);

    useEffect(() => {
        if (resultUpdatePaidItem === null) return;
        console.log("resultUpdatePaidItem", resultUpdatePaidItem);

        const paidItems = resultUpdatePaidItem?.rent?.rentPaidItems;
        const paidItemsPayments = resultUpdatePaidItem?.rent?.rentPayments.filter((payment: Types.RentPaymentDto) => payment?.rentPaymentType === "PAID_ITEM");
        const tableItem = paidItems.find((item: Types.RentPaidItemDto) => item?.rentPaidItemType === "TABLE_CHAIR");
        const beddingItem = paidItems.find((item: Types.RentPaidItemDto) => item?.rentPaidItemType === "BEDDING_SET");
        const tablePayment = paidItemsPayments.find((payment: Types.RentPaymentDto) => payment?.rentPaymentItem?.rentPaymentItemName?.includes("테이블"));
        const beddingPayment = paidItemsPayments.find((payment: Types.RentPaymentDto) => payment?.rentPaymentItem?.rentPaymentItemName?.includes("침구"));

        if (!tableItem && tablePayment) {
            requestDeletePayment(tablePayment);
        }
        if (!beddingItem && beddingPayment) {
            requestDeletePayment(beddingPayment);
        }

        paidItems?.forEach((item: Types.RentPaidItemDto) => {
            if ((item?.rentPaidItemType === "TABLE_CHAIR" && tablePayment) || (item?.rentPaidItemType === "BEDDING_SET" && beddingPayment)) {
                const payment: any = {
                    rentPaymentId: item?.rentPaidItemType === "TABLE_CHAIR" ? tablePayment?.rentPaymentId : beddingPayment?.rentPaymentId,
                    basicPrice: item?.price,
                    amount: item?.price,
                    rentPaymentItem: {
                        rentPaymentItemName: item?.rentPaidItemName,
                    },
                };
                requestUpdatePaidItemPayment(payment);
            } else if ((item?.rentPaidItemType === "TABLE_CHAIR" && !tablePayment) || (item?.rentPaidItemType === "BEDDING_SET" && !beddingPayment)) {
                const payment: any = {
                    rent: resultUpdatePaidItem?.rent,
                    rentPaymentType: "PAID_ITEM",
                    basicPrice: item?.price,
                    paymentMethod: "OFFLINE_BANK",
                    adjustedPrice: 0,
                    amount: item?.price,
                    rentPaymentItem: {
                        rentPaymentItemName: item?.rentPaidItemName,
                    },
                };
                requestAddPaidItemPayment(payment);
            }
        });
    }, [resultUpdatePaidItem]);

    useEffect(() => {
        if (errorUpdatePaidItem === null) return;

        console.log("errorUpdatePaidItem", errorUpdatePaidItem);
    }, [errorUpdatePaidItem]);

    //delete paid item payment
    const requestAxiosDeletePayment = async (rentPayment: Types.RentPaymentDto) => {
        const response = await utilAxiosWithAuth().delete(Request.RENT_PAYMENT_URL + rentPayment.rentPaymentId);
        return response.data;
    };

    const {
        loading: loadingDeletePayment,
        error: errorDeletePayment,
        data: resultDeletePayment,
        execute: requestDeletePayment,
    } = useAsyncAxios(requestAxiosDeletePayment);

    useEffect(() => {
        if (resultDeletePayment === null) return;

        console.log("resultDeletePayment", resultDeletePayment);
        requestGetRents();
    }, [resultDeletePayment]);

    useEffect(() => {
        if (errorDeletePayment === null) return;
        console.log("errorDeletePayment", errorDeletePayment);
    }, [errorDeletePayment]);

    //add paid item payment
    const requestAxiosAddPaidItemPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddPaidItemPayment,
        error: errorAddPaidItemPayment,
        data: resultAddPaidItemPayment,
        execute: requestAddPaidItemPayment,
    } = useAsyncAxios(requestAxiosAddPaidItemPayment);

    useEffect(() => {
        if (resultAddPaidItemPayment === null) return;

        console.log("resultAddPaidItemPayment", resultAddPaidItemPayment);
        requestGetRents();
    }, [resultAddPaidItemPayment]);

    useEffect(() => {
        if (errorAddPaidItemPayment === null) return;

        console.log("errorAddPaidItemPayment", errorAddPaidItemPayment);
    }, [errorAddPaidItemPayment]);

    //update paid item payment
    const requestAxiosUpdatePaidItemPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().put(Request.RENT_PAYMENT_URL + payment.rentPaymentId, payment);
        return response.data;
    };

    const {
        loading: loadingUpdatePaidItemPayment,
        error: errorUpdatePaidItemPayment,
        data: resultUpdatePaidItemPayment,
        execute: requestUpdatePaidItemPayment,
    } = useAsyncAxios(requestAxiosUpdatePaidItemPayment);

    useEffect(() => {
        if (resultUpdatePaidItemPayment === null) return;

        console.log("resultUpdatePaidItemPayment", resultUpdatePaidItemPayment);
        requestGetRents();
    }, [resultUpdatePaidItemPayment]);

    useEffect(() => {
        if (errorUpdatePaidItemPayment === null) return;

        console.log("errorUpdatePaidItemPayment", errorUpdatePaidItemPayment);
    }, [errorUpdatePaidItemPayment]);

    //add delayed arrive payment
    const requestAxiosAddDelayedArrivePayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddDelayedArrivePayment,
        error: errorAddDelayedArrivePayment,
        data: resultAddDelayedArrivePayment,
        execute: requestAddDelayedArrivePayment,
    } = useAsyncAxios(requestAxiosAddDelayedArrivePayment);

    useEffect(() => {
        if (resultAddDelayedArrivePayment === null) return;

        console.log("resultAddDelayedArrivePayment", resultAddDelayedArrivePayment);
        requestGetRents();
    }, [resultAddDelayedArrivePayment]);

    useEffect(() => {
        if (errorAddDelayedArrivePayment === null) return;

        console.log("errorAddDelayedArrivePayment", errorAddDelayedArrivePayment);
    }, [errorAddDelayedArrivePayment]);

    //add battery payment
    const requestAxiosAddBatteryPayment = async (payment: any) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_ADD_URL, payment);
        return response.data;
    };

    const {
        loading: loadingAddBatteryPayment,
        error: errorAddBatteryPayment,
        data: resultAddBatteryPayment,
        execute: requestAddBatteryPayment,
    } = useAsyncAxios(requestAxiosAddBatteryPayment);

    useEffect(() => {
        if (resultAddBatteryPayment === null) return;

        console.log("resultAddBatteryPayment", resultAddBatteryPayment);
        currentRent && addDelayedArrivePayment(currentRent);
        requestGetRents();
    }, [resultAddBatteryPayment]);

    useEffect(() => {
        if (errorAddBatteryPayment === null) return;
        console.log("errorAddBatteryPayment", errorAddBatteryPayment);
    }, [errorAddBatteryPayment]);

    const addBatteryPayment = (rentInfo: Types.RentDto) => {
        if (rentInfo.rentPayments.find((rentPayment) => rentPayment.rentPaymentItem?.rentPaymentCategory?.rentPaymentCategoryName.includes("배터리 미충전"))) {
            console.log("penalty battery already exist");
            return false;
        }

        if (rentInfo.startBatterySoc && rentInfo.endBatterySoc) {
            if (rentInfo.endBatterySoc >= Common.BATTERY_MAX_LIMIT) return false;

            // const startSoc = rentInfo.startBatterySoc > Common.BATTERY_MAX_LIMIT ? Common.BATTERY_MAX_LIMIT : rentInfo.startBatterySoc;
            const startSoc = rentInfo.startBatterySoc;

            if (startSoc - rentInfo.endBatterySoc >= 1) {
                const batteryPaymentItem = paymentItems.find((paymentItem) =>
                    paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("배터리 미충전")
                );
                if (batteryPaymentItem === null || batteryPaymentItem === undefined) return false;

                const penaltySoc = Math.floor(startSoc - rentInfo.endBatterySoc);
                const payment: any = {
                    rent: rentInfo,
                    rentPaymentType: "PENALTY",
                    rentPaymentItem: batteryPaymentItem,
                    basicPrice: penaltySoc * Common.BATTERY_PENALTY,
                    paymentMethod: "OFFLINE_BANK",
                    adjustedPrice: 0,
                    amount: penaltySoc * Common.BATTERY_PENALTY,
                };

                requestAddBatteryPayment(payment);
                return true;
            }
        }

        return false;
    };

    const addDelayedArrivePayment = (rentInfo: Types.RentDto) => {
        if (rentInfo.rentPayments.find((rentPayment) => rentPayment.rentPaymentItem?.rentPaymentCategory?.rentPaymentCategoryName.includes("반납 지연"))) {
            console.log("penalty delay arrive already exist");
            return false;
        }

        if (rentInfo.arrivedDate !== null && rentInfo.arrivedDate !== undefined && rentInfo.usedDate !== null && rentInfo.usedDate !== undefined) {
            const originalArrivedDate = moment(rentInfo.endDate + Common.DEFAULT_ARRIVED_TIME);
            const realArrivedDate = moment(Utils.utcToLocalTime(rentInfo.arrivedDate, Common.FORMAT_DATE_TIME));
            const minutes = realArrivedDate.diff(originalArrivedDate, "minutes");

            let delayedPaymentItem = null;
            if (minutes > 0 && minutes <= 60) {
                delayedPaymentItem = paymentItems.find(
                    (paymentItem) =>
                        paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("반납 지연") &&
                        paymentItem.rentPaymentItemName.includes("1시간 이내 지연 시")
                );
            } else if (minutes > 60 && minutes <= 120) {
                delayedPaymentItem = paymentItems.find(
                    (paymentItem) =>
                        paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("반납 지연") &&
                        paymentItem.rentPaymentItemName.includes("1시간 초과 2시간 내 지연 시")
                );
            } else if (minutes > 120) {
                delayedPaymentItem = paymentItems.find(
                    (paymentItem) =>
                        paymentItem.rentPaymentCategory.rentPaymentCategoryName.includes("반납 지연") &&
                        paymentItem.rentPaymentItemName.includes("2시간 초과 지연 시")
                );
            }

            if (delayedPaymentItem === null || delayedPaymentItem === undefined) return false;

            const payment: any = {
                rent: rentInfo,
                rentPaymentType: "PENALTY",
                rentPaymentItem: delayedPaymentItem,
                basicPrice: Utils.getPaymentItemAmount(rentInfo, delayedPaymentItem),
                paymentMethod: "OFFLINE_BANK",
                adjustedPrice: 0,
                amount: Utils.getPaymentItemAmount(rentInfo, delayedPaymentItem),
            };

            requestAddDelayedArrivePayment(payment);
            return true;
        }

        return false;
    };

    //cancel payment
    const requestAxiosCancelPayment = async (rentPaymentId: number, cancelAmount: number) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_PAYMENT_CANCEL_URL + rentPaymentId + "?cancelAmount=" + cancelAmount);
        return response.data;
    };

    const {
        loading: loadingCancelPayment,
        error: errorCancelPayment,
        data: resultCancelPayment,
        execute: requestCancelPayment,
    } = useAsyncAxios(requestAxiosCancelPayment);

    useEffect(() => {
        if (resultCancelPayment === null) return;

        console.log("resultCancelPayment", resultCancelPayment);
        requestCancelRent(resultCancelPayment.rentPayment.rent.rentId);
    }, [resultCancelPayment]);

    useEffect(() => {
        if (errorCancelPayment === null) return;

        console.log("errorPaymentCarComplete", errorCancelPayment);
    }, [errorCancelPayment]);

    //cancel rent
    const requestAxiosCancelRent = async (rentId: number) => {
        const response = await utilAxiosWithAuth().post(Request.RENT_URL + rentId + Request.RENT_CANCEL_URL);
        return response.data;
    };

    const { loading: loadingCancelRent, error: errorCancelRent, data: resultCancelRent, execute: requestCancelRent } = useAsyncAxios(requestAxiosCancelRent);

    useEffect(() => {
        if (resultCancelRent === null) return;
        setCurrentRent(undefined);
        requestGetRents();
    }, [resultCancelRent]);

    useEffect(() => {
        if (errorCancelRent === null) return;

        console.log("errorCancelRent", errorCancelRent);
    }, [errorCancelRent]);

    const displayReturnPayment = () => {
        if (cancelRent) {
            const insuranceAmount = cancelRent?.rentInsurance?.price || 0;
            const paidItemAmount = cancelRent?.rentPaidItems?.reduce((total, item) => total + item.price, 0) || 0;
            const cancelAmount = getCancelAmount(cancelRent.startDate, (getPayment(cancelRent)?.amount || 0) - insuranceAmount - paidItemAmount);
            const returnAmount = (getPayment(cancelRent)?.amount || 0) - cancelAmount;

            return (
                <>
                    <p>결제금액 : {Utils.convertCurrencyKorean(getPayment(cancelRent)?.amount || 0)}</p>
                    <p>
                        위약금 : {Utils.convertCurrencyKorean(cancelAmount)} &#40;{getCancelPercent(cancelRent.startDate)} %&#41;
                    </p>
                    <p>환불금액 : {Utils.convertCurrencyKorean(returnAmount)}</p>
                </>
            );
        } else return null;
    };

    const onCancelRent = (rent: Types.RentDto) => {
        setOpenModalCancelReservation(true);
        setCancelRent(rent);
    };

    const onUseRent = (rent: Types.RentDto) => {
        requestUseRent(rent.rentId);
    };

    const onArrivedRent = (rent: Types.RentDto) => {
        requestArrivedRent(rent.rentId);
    };

    const onChangeRentCar = (rent: Types.RentDto, rentCarId: number) => {
        requestChangeRentCar(rent.rentId, rentCarId);
    };

    const onChangeRentDetail = (rent: Types.RentDto, memo: string, checkedUsagePolicy: boolean, checkedExitPolicy: boolean) => {
        requestChangeRentDetail(rent.rentId, memo, checkedUsagePolicy, checkedExitPolicy);
    };

    const onUpdatePaidItem = (rent: Types.RentDto, paidItemIds: Array<number>) => {
        requestUpdatePaidItem(rent.rentId, paidItemIds);
    };

    const getPayment = (rent: Types.RentDto) => {
        return rent?.rentPayments?.find((payment) => payment?.rentPaymentType === "RENT");
    };

    const getCancelPercent = (startDate: string) => {
        const days = moment(startDate).diff(moment().subtract(1, "day"), "days");
        let percent = 0;

        if (days >= 3) {
            if (days <= 5) {
                percent = 20;
            } else if (days <= 10) {
                percent = 50;
            } else {
                percent = 100;
            }

            return 100 - percent;
        }
        return 100;
    };

    const getCancelAmount = (startDate: string, rentalPrice: number) => {
        const days = moment(startDate).diff(moment().subtract(1, "day"), "days");
        let percent = 0;

        if (days >= 3) {
            if (days <= 5) {
                percent = 20;
            } else if (days <= 10) {
                percent = 50;
            } else {
                percent = 100;
            }

            return (rentalPrice / 100) * (100 - percent);
        }
        return (rentalPrice / 100) * 100;
    };

    const handleCancelReservationModelOk = () => {
        setOpenModalCancelReservation(false);

        if (cancelRent !== undefined) {
            const insuranceAmount = cancelRent?.rentInsurance?.price || 0;
            const paidItemAmount = cancelRent?.rentPaidItems?.reduce((total, item) => total + item.price, 0) || 0;
            const cancelAmount = getCancelAmount(cancelRent.startDate, (getPayment(cancelRent)?.amount || 0) - insuranceAmount - paidItemAmount);
            const returnAmount = (getPayment(cancelRent)?.amount || 0) - cancelAmount;

            requestCancelPayment(getPayment(cancelRent)?.rentPaymentId, returnAmount);
        }
    };

    return (
        <>
            <DataTable hasScroll={false} columns={columns} rowKey="rentId" dataSource={rents} onRow={(value) => setCurrentRent(value)} />{" "}
            <Space direction="vertical" size={30}>
                {currentRent && (
                    <RentDetail
                        rent={currentRent}
                        onCancelRent={onCancelRent}
                        onUseRent={onUseRent}
                        onArrivedRent={onArrivedRent}
                        onChangeRentCar={onChangeRentCar}
                        onChangeRentDetail={onChangeRentDetail}
                        onUpdatePaidItem={onUpdatePaidItem}
                    />
                )}
                {currentRent?.additionalDriver && <RentUserDetail user={currentRent?.additionalDriver} additionalDriverMode={true} />}
                {currentRent && <RentImageViewer rent={currentRent} />}
                {currentRent && <RentPaymentViewer rent={currentRent} onUpdateRentInfo={() => requestGetRents()} />}
            </Space>
            {openModalCancelReservation && cancelRent && (
                <Modal
                    centered
                    title="예약취소"
                    open={openModalCancelReservation}
                    onOk={handleCancelReservationModelOk}
                    okText="예약취소"
                    onCancel={() => {
                        setOpenModalCancelReservation(false);
                        // setCurrentRent(undefined);
                    }}
                    cancelText="이전"
                >
                    {displayReturnPayment()}
                </Modal>
            )}
        </>
    );
}
export default MyRent;
